import {FormikErrors} from 'formik';
import React, {ReactElement, ReactNode} from 'react';
import {FormattedMessage} from 'react-intl';
import {cx} from '@emotion/css';
import isElementInViewport from '@fo/shared-checks/isElementInViewport';
import flattenObject from '@fo/shared-helpers/flattenObject';
import {fonts} from '@fo/shared/styles/variables';
import Headers, {HeaderType} from '@fo/designSystem/Text/Headers/Headers';

import styles from './PageErrors.styles';

interface Props<FormValues> {
  errors: FormikErrors<FormValues>;
}

function getErrorButtonFromErrorMessage(
  errorKey: string,
  handleButtonClick: (fieldName: string) => void,
  errorMessage: string
): ReactNode {
  try {
    const potentialNestedObject = JSON.parse(errorMessage);
    return Object.keys(potentialNestedObject).map((nestedKey) =>
      getErrorButtonFromErrorMessage(`${errorKey}.${nestedKey}`, handleButtonClick, potentialNestedObject[nestedKey])
    );
  } catch (e) {
    // it's not nested
    return (
      <li key={errorKey}>
        <button
          className={cx(styles.messageButton)}
          type="button"
          onClick={() => {
            handleButtonClick(errorKey);
          }}
        >
          {errorMessage}
        </button>
      </li>
    );
  }
}

function PageErrors<FormValues>({errors}: Props<FormValues>): ReactElement {
  const flattenedErrors = flattenObject(errors);
  const scrollToField = (errorKey: string) => {
    // errorKey corresponds to the name of the field in the form
    const elementToScrollIn = document.querySelector(`label[for="${errorKey}"]`);
    if (!elementToScrollIn || isElementInViewport(elementToScrollIn)) {
      return;
    }

    elementToScrollIn.scrollIntoView();
  };

  return (
    <div className={cx(styles.container)} data-testid="ui.pageErrors">
      <Headers headerType={HeaderType.H4} alignment={fonts.alignment.LEFT} className={cx(styles.header)}>
        <FormattedMessage id="phoenix.errors.multi.header" />
      </Headers>
      <ul className={cx(styles.list)}>
        {Object.keys(flattenedErrors).map((errorKey) => {
          const errorMessage = flattenedErrors[errorKey];
          return errorMessage ? getErrorButtonFromErrorMessage(errorKey, scrollToField, errorMessage) : null;
        })}
      </ul>
    </div>
  );
}

export default PageErrors;
